import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { catchError, mergeMap } from 'rxjs/operators';
import { ToastService } from 'src/app/services/toast.service';
import { Observable, throwError } from 'rxjs';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { SpinnerService } from '../Spinner/spinner.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { environment } from 'src/environments/environment';
import jwtDecode from 'jwt-decode';
import { APIService } from '../APIService/api.service';

@Injectable()
export class Logoutinterceptor implements HttpInterceptor {
  private oktaAuth = inject<OktaAuth>(OKTA_AUTH);
  private toastr = inject(ToastService);
  private router = inject(Router);
  private spinner = inject(SpinnerService);
  private apiService = inject(APIService);

  private apiUrl = environment.apiUrl;
  private sessionExpiredShown = false;

  // Static method to reset session expired flags globally
  public static resetSessionExpiredFlags() {
    // Reset the global flag for navigateToLogin function
    (window as any).sessionExpiredNavigating = false;
  }

  // Method to reset session expired flag when authentication is successful
  public resetSessionExpiredFlag() {
    this.sessionExpiredShown = false;
    Logoutinterceptor.resetSessionExpiredFlags();
  }

  private getRefreshToken() {
    const refreshToken = localStorage.getItem('refresh');
    const jwtExp = localStorage.getItem('jwt_exp');

    return {
      passphrase: refreshToken || '',
      expiresAt: jwtExp ? parseInt(jwtExp) : 0,
    };
  }

  private getToken(): any {
    try {
      const jwtToken = localStorage.getItem('jwt');

      // Check if token exists and is not empty
      if (!jwtToken || jwtToken.trim() === '' || jwtToken === 'null' || jwtToken === 'undefined') {
        // Don't log warnings during normal logout/login flow
        return null;
      }

      // Basic JWT format validation (should have 3 parts separated by dots)
      const tokenParts = jwtToken.split('.');
      if (tokenParts.length !== 3) {
        console.warn('JWT token does not have proper format (expected 3 parts)');
        return null;
      }

      // Try to decode the token
      const decodedToken = jwtDecode(jwtToken);

      // Validate that decoded token has required properties
      if (!decodedToken || typeof decodedToken !== 'object') {
        console.warn('Decoded JWT token is not a valid object');
        return null;
      }

      return decodedToken;
    } catch (error) {
      console.error('JWT decode error in interceptor:', error);
      console.warn('Clearing invalid JWT token from localStorage');

      // Clear the invalid token to prevent repeated errors
      localStorage.removeItem('jwt');
      localStorage.removeItem('jwt_exp');
      localStorage.removeItem('refresh');

      return null;
    }
  }

  private navigateToLoginDirect() {
    // Prevent multiple session expired toasters from showing
    if (this.sessionExpiredShown) {
      return;
    }

    this.sessionExpiredShown = true;

    /*this.toastr.warning(
      'Please log in again.',
      'Your session has expired.'
    );
    */

    setTimeout(() => {
      this.router.navigateByUrl('login');
      // Reset the flag after navigation
      setTimeout(() => {
        this.sessionExpiredShown = false;
      }, 1000);
    }, 2000);
  }

  private refreshLogin(email: string, refreshToken: string): Observable<any> {
    // Instead of making HTTP call here, we'll redirect to login
    // This avoids the circular dependency
    this.navigateToLoginDirect();
    return throwError('Token refresh required - redirecting to login');
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): import('rxjs').Observable<HttpEvent<any>> {
    let refresh = this.getRefreshToken();
    const jwtExpString = localStorage.getItem('jwt_exp');
    let tokenExpired = false;

    if (jwtExpString && !isNaN(parseInt(jwtExpString))) {
      tokenExpired = parseInt(jwtExpString) < Math.floor(Date.now() / 1000);
    } else {
      // If we don't have a valid expiry time, consider the token expired
      tokenExpired = true;
    }

    // Handle Okta API requests
    if (req.url.indexOf('api/okta/') != -1) {
      const accessToken = this.oktaAuth.getAccessToken();
      let headers = new HttpHeaders();
      headers = headers
        .set('Content-Type', 'application/json; charset=utf-8')
        .set('Authorization', `Bearer ${accessToken}`);

      return next
        .handle(
          req.clone({
            headers,
          })
        )
        .pipe(
          catchError((err: HttpErrorResponse) => {
            return errorCatch(req, next, refresh, err, this.toastr, this.router, this.apiService);
          })
        );
    }

    // Skip token validation for login and other public endpoints
    if (
      req.url.indexOf('login') != -1 ||
      req.url.indexOf('i18n/') != -1 ||
      req.url.indexOf('register') != -1 ||
      req.url.indexOf('forgotpassword') != -1 ||
      req.url.indexOf('resetpassword') != -1 ||
      req.url.indexOf('password-reset') != -1
    ) {
      // For login and public endpoints, don't add JWT token or validate
      return next.handle(req).pipe(
        catchError((err: HttpErrorResponse) => {
          // Handle errors for login/logout requests differently
          if (req.url.indexOf('login') != -1) {
            console.warn(`Login/logout request failed: ${req.url}`, err);
            // Don't redirect to login for login/logout failures, just pass the error through
            return throwError(err);
          }
          return throwError(err);
        })
      );
    } // For all other requests, validate token
    const token = this.getToken();
    const jwtToken = localStorage.getItem('jwt');

    // Skip token validation for certain routes during authentication flow
    if (
      req.url.indexOf('login') !== -1 ||
      req.url.indexOf('callback') !== -1 ||
      req.url.indexOf('i18n/') !== -1 ||
      req.url.indexOf('/assets/') !== -1 ||
      req.url.indexOf('password-reset') !== -1
    ) {
      return next.handle(req);
    }

    // If we don't have a valid token, redirect to login for protected requests
    if (!token) {
      this.navigateToLoginDirect();
      return throwError('No valid token found - redirecting to login');
    }

    if (
      tokenExpired &&
      Math.floor(Date.now() / 1000) < refresh.expiresAt &&
      req.url.indexOf('login') == -1 &&
      req.url.indexOf('i18n/') == -1 &&
      token != null
    ) {
      let authObs = this.refreshLogin(token.email, refresh.passphrase);

      return authObs.pipe(
        mergeMap(() => {
          const refreshedJwtToken = localStorage.getItem('jwt');
          return next.handle(
            req.clone({
              headers: req.headers.set('Authorization', 'Bearer ' + (refreshedJwtToken || '')),
            })
          );
        })
      );
    } else {
      return next
        .handle(
          req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + (jwtToken || '')),
          })
        )
        .pipe(
          catchError((err: HttpErrorResponse) => {
            return errorCatch(req, next, refresh, err, this.toastr, this.router, this.apiService);
          })
        );
    }
  }
}

function errorCatch(
  req: HttpRequest<any>,
  next: HttpHandler,
  refresh: any,
  err: HttpErrorResponse,
  toastr: ToastService,
  router: Router,
  apiService: APIService
) {
  if (err.status == 401) {
    if (
      refresh.expiresAt > 0 &&
      Math.floor(Date.now() / 1000) < refresh.expiresAt &&
      req.url.indexOf('login') == -1 &&
      req.url.indexOf('i18n/') == -1
    ) {
      // Instead of trying to refresh token, redirect to login
      // This avoids the circular dependency
      navigateToLogin(toastr, router, apiService, undefined);
      return throwError(() => err);
    } else {
      if (req.url.indexOf('login') != -1) toastr.warning('Please log in again.', 'Incorrect login information');
      else {
        navigateToLogin(toastr, router, apiService, undefined);
      }
      return throwError(() => err);
    }
  } else {
    return throwError(() => err);
  }
}

export function navigateToLogin(
  toastr: any,
  router: any,
  routeOrApiService?: ActivatedRouteSnapshot | APIService,
  routeMaybe?: ActivatedRouteSnapshot
) {
  const apiService =
    routeOrApiService && 'sendRelease' in routeOrApiService ? (routeOrApiService as APIService) : undefined;
  const route =
    routeMaybe ||
    (routeOrApiService && 'url' in routeOrApiService ? (routeOrApiService as ActivatedRouteSnapshot) : undefined);

  // Use a simple flag to prevent multiple calls
  if ((window as any).sessionExpiredNavigating) {
    return;
  }

  (window as any).sessionExpiredNavigating = true;

  toastr.warning('Please log in again.', 'Your session has expired.');

  setTimeout(() => {
    if (route) {
      // Store redirect URL in localStorage instead of AuthService
      localStorage.setItem('redirectURL', route.url[0].path);
    }
    router.navigateByUrl('login');
    // Reset the flag after navigation
    setTimeout(() => {
      (window as any).sessionExpiredNavigating = false;
    }, 1000);
  }, 2000);
}
